/*
 * The character being made, held on this machine until it is submitted.
 *
 * Creation is seven steps of typed and chosen answers and the server learns
 * none of it until the Oath is sworn — so before this, a refresh, a mis-hit on
 * the browser's back button, or a laptop closing threw away everything the
 * player had written. Nothing about that is recoverable server-side, because
 * there is nothing on the server yet.
 *
 * localStorage rather than sessionStorage, deliberately: the loss a player
 * minds is not only the refresh but coming back to a closed tab. It is cleared
 * on a successful submit — after that the character is a row, not a draft.
 *
 * Every read is defensive. This is a blob written by an older build of the app
 * against a possibly different database, so the caller re-checks every id it
 * gets back (see restoreDraft in CharacterSubmit.vue) and this module only
 * promises to return an object or nothing at all.
 */

/* Versioned in the key, not in the payload: a shape change should make the old
   draft invisible rather than something a migration has to understand. */
const KEY = 'dx.creation.draft.v1';

/* A draft is a convenience, not a record. Past this it is more likely to be a
   forgotten attempt the player has stopped thinking about than work they want
   handed back, and restoring it silently would be the surprise. */
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

export function writeDraft({character, stepIndex, furthest}) {
    try {
        window.localStorage.setItem(KEY, JSON.stringify({
            savedAt: Date.now(),
            stepIndex,
            furthest,
            /* Through JSON so the reactive proxy is not what gets stored. */
            character: JSON.parse(JSON.stringify(character)),
        }));
    } catch {
        /* Private mode, a full quota, storage disabled by policy. Losing the
           draft is the status quo this module improves on — it is never a
           reason to fail the step the player is standing on. */
    }
}

export function readDraft() {
    try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) return null;

        const draft = JSON.parse(raw);
        if (!draft || typeof draft !== 'object' || !draft.character) return null;

        if (!Number.isFinite(draft.savedAt) || Date.now() - draft.savedAt > MAX_AGE_MS) {
            clearDraft();
            return null;
        }
        return draft;
    } catch {
        /* Unparseable means written by something this build does not know.
           Drop it rather than carry it. */
        clearDraft();
        return null;
    }
}

export function clearDraft() {
    try {
        window.localStorage.removeItem(KEY);
    } catch {
        /* As above. */
    }
}
